import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Bookmark, X, FileSpreadsheet, FileDown, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { fmt, fmt0, dateLabel, uid, monthLabel } from "../lib/format";
import { exportSearchXLSX, exportSearchPDF, fileSlug } from "../lib/receiptExport";
import { shareOrSave } from "../lib/reports";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { NativeSelect } from "./common";
import { ReceiptThumb } from "./Receipts";
import { allReceipts, filterReceipts, receiptTotals, monthlyTrend } from "../lib/receiptSearch";

const LIMIT = 60;
const EMPTY = { q: "", where: "all", from: "", to: "" };

function SavedViews({ current, onPick, list }) {
  const { state, update } = useStore();
  const views = state.savedReceiptSearches || [];
  const canSave = current.q.trim() || current.from || current.to || current.where !== "all";
  const save = () => {
    const name = window.prompt("Name this search", [current.q.trim(), current.from && current.to ? `${current.from} – ${current.to}` : ""].filter(Boolean).join(" · ") || "My search");
    if (!name?.trim()) return;
    update("Save receipt search", (d) => (d.savedReceiptSearches = [...(d.savedReceiptSearches || []), { id: uid(), name: name.trim().slice(0, 40), ...current }]));
    toast.success(`Saved "${name.trim()}"`);
  };
  const remove = (v) => update("Delete saved search", (d) => (d.savedReceiptSearches = d.savedReceiptSearches.filter((x) => x.id !== v.id)));
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="saved-searches">
      {views.map((v) => {
        const tot = receiptTotals(filterReceipts(list, v));
        return (
        <span key={v.id} className="inline-flex items-center rounded-full border bg-card text-xs" data-testid={`saved-search-${v.id}`}>
          <button className="pl-3 pr-1 py-1 hover:text-primary" onClick={() => onPick(v)} data-testid={`saved-search-open-${v.id}`}><Bookmark className="inline h-3 w-3 mr-1" />{v.name} <span className="text-muted-foreground num" data-testid={`saved-search-total-${v.id}`}>· {tot.receipts} · {fmt(tot.total)}</span></button>
          <button className="pr-2 pl-1 py-1 text-muted-foreground hover:text-expense" aria-label={`Delete ${v.name}`} onClick={() => remove(v)} data-testid={`saved-search-delete-${v.id}`}><X className="h-3 w-3" /></button>
        </span>
        );
      })}
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={save} disabled={!canSave} data-testid="save-search-button"><Bookmark className="h-3 w-3 mr-1" /> Save this search</Button>
    </div>
  );
}

const describe = (f) => [f.q.trim() && `“${f.q.trim()}”`, f.where !== "all" && (f.where === "attached" ? "attached only" : "inbox only"), (f.from || f.to) && `${f.from || "start"} to ${f.to || "today"}`].filter(Boolean).join(" · ") || "All receipts";

function SearchActions({ found, tot, title, f }) {
  const { getReceipt } = useStore();
  const [busy, setBusy] = useState(null);
  const pdf = async () => {
    setBusy("Starting…");
    try {
      const blob = await exportSearchPDF(found, title, tot, describe(f), getReceipt, (i, n) => setBusy(`Photo ${i}/${n}…`));
      const how = await shareOrSave(blob, `receipts-${fileSlug(title)}.pdf`, `Receipts — ${title}`);
      if (how) toast.success(`PDF ${how === "shared" ? "ready to send" : "downloaded"}: ${found.length} receipt${found.length === 1 ? "" : "s"} with photos`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't create the PDF");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="receipt-search-export">
      <Button size="sm" variant="outline" className="h-8" onClick={() => { exportSearchXLSX(found, title, tot); toast.success("Spreadsheet downloaded"); }} data-testid="receipt-search-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Spreadsheet</Button>
      <Button size="sm" variant="outline" className="h-8" onClick={pdf} disabled={!!busy} data-testid="receipt-search-export-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> <span data-testid="receipt-search-export-pdf-label">{busy || "PDF with photos"}</span></Button>
    </div>
  );
}

function SearchTrend({ found }) {
  const data = monthlyTrend(found).map((d) => ({ ...d, label: monthLabel(d.month, true) }));
  if (!data.length) return null;
  return (
    <div className="rounded-xl border p-3 mb-4" data-testid="receipt-search-trend">
      <p className="text-sm font-medium mb-2 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Month by month <span className="text-xs text-muted-foreground font-normal" data-testid="receipt-search-trend-months">({data.length} month{data.length === 1 ? "" : "s"})</span></p>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -10, right: 4, top: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmt0(v)} width={56} />
            <Tooltip formatter={(v, _n, p) => [`${fmt(v)} · ${p.payload.count} receipt${p.payload.count === 1 ? "" : "s"}`, "Total"]} />
            <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ReceiptSearch() {
  const { state } = useStore();
  const [f, setF] = useState(EMPTY);
  const [view, setView] = useState(null);
  const set = (k) => (v) => { setView(null); setF((x) => ({ ...x, [k]: v })); };
  const list = useMemo(() => allReceipts(state), [state]);
  const found = filterReceipts(list, f);
  const tot = receiptTotals(found);
  return (
    <div className="pb-24 md:pb-0" data-testid="receipt-search">
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Store, amount (e.g. 42.10) or note" className="pl-9" autoFocus data-testid="receipt-search-input" />
        </div>
        <NativeSelect value={f.where} onChange={set("where")} testid="receipt-search-filter"><option value="all">All receipts</option><option value="attached">Attached</option><option value="inbox">In inbox</option></NativeSelect>
        <div className="flex items-center gap-1 text-xs text-muted-foreground w-full sm:w-auto">
          <Input type="date" value={f.from} onChange={(e) => set("from")(e.target.value)} className="flex-1 min-w-0 sm:w-[150px] sm:flex-none" aria-label="From date" data-testid="receipt-search-from" />
          <span>to</span>
          <Input type="date" value={f.to} onChange={(e) => set("to")(e.target.value)} className="flex-1 min-w-0 sm:w-[150px] sm:flex-none" aria-label="To date" data-testid="receipt-search-to" />
          {(f.from || f.to) && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setView(null); setF((x) => ({ ...x, from: "", to: "" })); }} aria-label="Clear dates" data-testid="receipt-search-clear-dates"><X className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      <SavedViews current={f} list={list} onPick={(v) => { setView(v.name); setF({ q: v.q || "", where: v.where || "all", from: v.from || "", to: v.to || "" }); }} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        {view && <p className="text-sm font-semibold" data-testid="receipt-search-view-name">{view}</p>}
        <p className="text-xs text-muted-foreground" data-testid="receipt-search-count">{found.length} of {list.length} receipt photo{list.length === 1 ? "" : "s"}{found.length > LIMIT ? ` · showing first ${LIMIT}` : ""}</p>
        {found.length > 0 && <p className="text-sm" data-testid="receipt-search-totals"><span className="font-semibold num" data-testid="receipt-search-total-amount">{fmt(tot.total)}</span> <span className="text-muted-foreground">total · {[tot.transactions && `${tot.transactions} transaction${tot.transactions === 1 ? "" : "s"}`, found.some((r) => !r.tx) && `${found.filter((r) => !r.tx).length} in inbox`].filter(Boolean).join(" + ")}</span></p>}
        {found.length > 0 && <div className="sm:ml-auto"><SearchActions found={found} tot={tot} title={view || describe(f)} f={f} /></div>}
      </div>
      {found.length > 0 && <SearchTrend found={found} />}
      {found.length ? (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {found.slice(0, LIMIT).map((r) => (
            <li key={r.key} className="rounded-xl border bg-card overflow-hidden" data-testid={`receipt-result-${r.key}`}>
              <ReceiptThumb meta={r.meta} className="h-32 w-full" />
              <div className="p-2 text-xs space-y-0.5">
                <p className="font-medium truncate" title={r.title}>{r.title}</p>
                {r.store && r.store !== r.title && <p className="truncate text-muted-foreground">Receipt: {r.store}</p>}
                <p className="text-muted-foreground">{r.date ? dateLabel(r.date) : "No date"}{r.amount != null && <> · <span className="num">{fmt(r.amount)}</span></>}</p>
                {r.note && <p className="truncate text-muted-foreground" title={r.note}>{r.note}</p>}
                {r.tx ? <Link to={`/transactions?tx=${r.tx.id}`} className="text-primary underline" data-testid={`receipt-result-open-${r.key}`}>Open transaction</Link> : <span className="inline-block rounded-full bg-warning-soft px-2 py-0.5" data-testid={`receipt-result-inbox-${r.key}`}>In inbox</span>}
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground" data-testid="receipt-search-empty">{list.length ? "No receipts match that search." : "No receipt photos yet. Snap one or attach one to a transaction."}</p>}
    </div>
  );
}
